import { DatePicker as Calendar } from '@mui/x-date-pickers';
import moment from 'moment';
import { useMemo } from 'react';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';

export interface IDeliveryDays {
  delivery_days?: string[];
}

interface IDatePickerProps {
  pwId?: string;
  width?: string;
  date: moment.Moment | null;
  setDate: (date: moment.Moment | null) => void;
  label?: string;
  deliveryDays?: IDeliveryDays;
  disabled?: boolean;
  textFieldSlotProps?: Record<string, unknown>;
}

const DatePicker = ({
  pwId,
  width,
  date,
  setDate,
  label,
  deliveryDays,
  disabled,
  textFieldSlotProps,
}: IDatePickerProps) => {
  const { isDesktop } = useBreakpointFlag();

  const today = moment().startOf('day');

  // Set of allowed delivery dates (from backend) for fast lookup
  const allowedDatesSet = useMemo(
    () =>
      new Set(
        (deliveryDays?.delivery_days ?? []).map((dateStr) =>
          moment(dateStr, 'DD-MM-YYYY').format('YYYY-MM-DD')
        )
      ),
    [deliveryDays]
  );

  const mergedTextFieldProps = useMemo<Record<string, unknown>>(
    () => ({
      size: 'small',
      label: label || '',
      ...textFieldSlotProps,
    }),
    [label, textFieldSlotProps]
  );

  const shouldDisableDate = (day: moment.Moment) => {
    const selectedDate = moment(day).startOf('day');

    // Always disable dates before and including today
    if (selectedDate.isSameOrBefore(today, 'day')) {
      return true;
    }

    // Only allow dates returned by the backend
    return !allowedDatesSet.has(selectedDate.format('YYYY-MM-DD'));
  };

  return (
    <Calendar
      data-testid={pwId}
      value={date}
      disabled={disabled}
      onChange={(newValue) => setDate(newValue)}
      shouldDisableDate={shouldDisableDate}
      sx={{
        width: width ? width : '300rem',
        '& .MuiInputBase-input': {
          height: '20rem',
          fontSize: isDesktop ? '16px' : '15px',
        },
        '& svg': {
          fontSize: isDesktop ? '25rem' : '20rem',
        },
      }}
      slotProps={{
        actionBar: { actions: ['clear'] },
        field: { readOnly: true },
        textField: mergedTextFieldProps,
      }}
    />
  );
};
export default DatePicker;
